import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { CoachEInvoiceProfile } from '../../types/coachEInvoiceProfile';
import { saveCoachEInvoiceProfile } from '../../services/coachEInvoiceFirebaseService';
import { isValidIban, normalizeIban } from '../../utils/pe-papers/ibanValidation';
import { assessCoachEInvoiceProfile } from '../../utils/coach/coachEInvoiceProfileUtils';

export interface CoachEInvoiceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  coachId: string;
  profile: CoachEInvoiceProfile;
  /** Wenn Profil leer: Felder vorausfüllen (z. B. Owner-Stammdaten). */
  suggestedDefaults?: CoachEInvoiceProfile | null;
  onSaved: (profile: CoachEInvoiceProfile) => void;
}

const inputClass =
  'w-full rounded-xl border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white focus:border-[#E6C299] focus:outline-none';
const labelClass = 'mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400';

const mergeSuggestedDefaults = (
  profile: CoachEInvoiceProfile,
  suggested?: CoachEInvoiceProfile | null
): CoachEInvoiceProfile => {
  if (!suggested) return profile;
  if (assessCoachEInvoiceProfile(profile).complete) return profile;
  const pick = (key: keyof CoachEInvoiceProfile) => {
    const cur = profile[key];
    if (typeof cur === 'string') return cur.trim() ? cur : (suggested[key] as string);
    if (typeof cur === 'boolean') return cur;
    if (typeof cur === 'number') return cur || (suggested[key] as number);
    return cur ?? suggested[key];
  };
  return {
    displayName: pick('displayName') as string,
    accountHolder: pick('accountHolder') as string,
    bankName: pick('bankName') as string,
    iban: pick('iban') as string,
    bic: pick('bic') as string,
    taxNumber: pick('taxNumber') as string,
    vatId: pick('vatId') as string,
    street: pick('street') as string,
    zip: pick('zip') as string,
    city: pick('city') as string,
    country: pick('country') as string,
    email: pick('email') as string,
    phone: pick('phone') as string,
    reverseCharge: false,
    defaultPaymentTermsDays: profile.defaultPaymentTermsDays || suggested.defaultPaymentTermsDays || 14,
  };
};

const CoachEInvoiceSettingsModal: React.FC<CoachEInvoiceSettingsModalProps> = ({
  isOpen,
  onClose,
  coachId,
  profile,
  suggestedDefaults = null,
  onSaved,
}) => {
  const [local, setLocal] = useState<CoachEInvoiceProfile>(profile);
  const [saveError, setSaveError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSaveError('');
      return;
    }
    setLocal(mergeSuggestedDefaults(profile, suggestedDefaults));
  }, [isOpen, profile, suggestedDefaults]);

  if (!isOpen) return null;

  const patch = <K extends keyof CoachEInvoiceProfile>(key: K, value: CoachEInvoiceProfile[K]) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const normalized: CoachEInvoiceProfile = {
      ...local,
      displayName: local.displayName.trim(),
      accountHolder: (local.accountHolder || local.displayName).trim(),
      bankName: local.bankName.trim(),
      iban: normalizeIban(local.iban),
      bic: (local.bic || '').replace(/\s+/g, '').toUpperCase(),
      taxNumber: local.taxNumber.trim(),
      vatId: local.vatId.trim(),
      street: local.street.trim(),
      zip: local.zip.trim(),
      city: local.city.trim(),
      country: (local.country || 'DE').trim().toUpperCase(),
      email: local.email.trim().toLowerCase(),
      phone: local.phone.trim(),
      reverseCharge: false,
      defaultPaymentTermsDays: local.defaultPaymentTermsDays || 14,
    };

    if (!isValidIban(normalized.iban)) {
      setSaveError('Bitte eine gültige IBAN eintragen.');
      return;
    }
    const check = assessCoachEInvoiceProfile(normalized);
    if (!check.complete) {
      setSaveError(`Noch unvollständig: ${check.missing.join(', ')}`);
      return;
    }

    setBusy(true);
    setSaveError('');
    const result = await saveCoachEInvoiceProfile(coachId, normalized);
    setBusy(false);
    if (!result.savedRemote) {
      setSaveError('Speichern in Firebase fehlgeschlagen (Berechtigung?).');
      return;
    }
    onSaved(normalized);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100020] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md">
      <div
        className="relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-[#E6C299]/30 bg-slate-950 text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-lg border border-slate-600 p-2 text-slate-400 hover:text-white"
          aria-label="Schließen"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b border-slate-700 px-6 py-5 pr-14">
          <h3 className="text-lg font-black uppercase tracking-wider text-[#E6C299]">
            Meine Rechnungsdaten (E-Rechnung)
          </h3>
          <p className="mt-2 text-xs text-slate-400">
            Deine Ausstellerdaten für Mandanten- und Honorarrechnungen (IBAN, Steuer, Adresse).
            Erst wenn vollständig gespeichert, werden ZUGFeRD-E-Rechnungen erzeugt.
          </p>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
          <label className="block">
            <span className={labelClass}>Firmenname / Name *</span>
            <input
              className={inputClass}
              value={local.displayName}
              onChange={(e) => patch('displayName', e.target.value)}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Straße *</span>
            <input className={inputClass} value={local.street} onChange={(e) => patch('street', e.target.value)} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelClass}>PLZ *</span>
              <input className={inputClass} value={local.zip} onChange={(e) => patch('zip', e.target.value)} />
            </label>
            <label className="block">
              <span className={labelClass}>Ort *</span>
              <input className={inputClass} value={local.city} onChange={(e) => patch('city', e.target.value)} />
            </label>
          </div>
          <label className="block">
            <span className={labelClass}>E-Mail *</span>
            <input
              type="email"
              className={inputClass}
              value={local.email}
              onChange={(e) => patch('email', e.target.value)}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Telefon</span>
            <input className={inputClass} value={local.phone} onChange={(e) => patch('phone', e.target.value)} />
          </label>
          <label className="block">
            <span className={labelClass}>Kontoinhaber *</span>
            <input
              className={inputClass}
              value={local.accountHolder}
              onChange={(e) => patch('accountHolder', e.target.value)}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Bank</span>
            <input className={inputClass} value={local.bankName} onChange={(e) => patch('bankName', e.target.value)} />
          </label>
          <label className="block">
            <span className={labelClass}>IBAN *</span>
            <input
              className={inputClass}
              value={local.iban}
              onChange={(e) => patch('iban', e.target.value)}
              placeholder="DE…"
            />
          </label>
          <label className="block">
            <span className={labelClass}>BIC</span>
            <input className={inputClass} value={local.bic} onChange={(e) => patch('bic', e.target.value)} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelClass}>Steuernummer</span>
              <input
                className={inputClass}
                value={local.taxNumber}
                onChange={(e) => patch('taxNumber', e.target.value)}
              />
            </label>
            <label className="block">
              <span className={labelClass}>USt-IdNr.</span>
              <input className={inputClass} value={local.vatId} onChange={(e) => patch('vatId', e.target.value)} />
            </label>
          </div>
          <p className="rounded-xl border border-slate-700 bg-slate-900/50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
            Ausland / Reverse Charge (§ 13b) wird nicht hier gesetzt, sondern bei jeder
            Mandanten-Rechnung im Rechnungs-Center (nur wenn nötig). Standard bleibt 19 % MwSt.
          </p>

          {saveError ? <p className="text-xs text-red-400">{saveError}</p> : null}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-slate-600 py-3 text-xs font-bold uppercase tracking-widest text-slate-400"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleSave()}
              className="flex-1 rounded-xl bg-[#E6C299] py-3 text-xs font-black uppercase tracking-widest text-black disabled:opacity-50"
            >
              {busy ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachEInvoiceSettingsModal;
